import React from 'react';
import Lottie from 'lottie-react';
import { Button, Typography } from 'components';
import { CameraOutlined, CloseOutlined, PictureOutlined } from '@ant-design/icons';
import { ArrowLeftIcon } from 'components/common/icons/ArrowLeftIcon';
import { ArrowRightIcon } from 'components/common/icons/ArrowRightIcon';
import { useUploadDeliveryNote } from './useUploadDeliveryNote';
import loadingAnimation from 'assets/animations/animations.json';
import styles from './styles.module.scss';

const { Title, Text } = Typography;

export const UploadDeliveryNote: React.FC = () => {
  const {
    images,
    currentImageIndex,
    isProcessing,
    isLoading,
    orderIdentifier,
    isCameraActive,
    isMobile,
    fileInputRef,
    videoRef,
    canvasRef,
    handleFileSelect,
    handleCapture,
    handleOpenGallery,
    handleRetake,
    handleCaptureNextPage,
    handlePrevious,
    handleNext,
    handleDone,
    handleCancel,
    handleTouchStart,
    handleTouchMove,
    handleTouchEnd,
  } = useUploadDeliveryNote();

  const hasImages = images.length > 0 && images[currentImageIndex];
  const validImagesCount = images.filter((img) => img !== '').length;

  // On mobile with no images, show a simple prompt (native camera will open automatically)
  if (isMobile && images.length === 0) {
    return (
      <div className={styles.container}>
        <div className={styles.header}>
          <Button type="text" icon={<CloseOutlined />} onClick={handleCancel} className={styles.closeButton} />
          <Title level={4} className={styles.title}>
            Upload Delivery Note
          </Title>
        </div>

        <div className={styles.capturePrompt}>
          <CameraOutlined className={styles.cameraIcon} />
          <Text className={styles.promptText}>Capture or select delivery note images</Text>
          <Button
            type="primary"
            size="large"
            icon={<CameraOutlined />}
            onClick={() => fileInputRef.current?.click()}
            className={styles.captureButton}
          >
            Take Photo or Choose from Gallery
          </Button>
        </div>

        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          onChange={handleFileSelect}
          style={{ display: 'none' }}
        />
      </div>
    );
  }

  // Camera capture screen (desktop only)
  if (!isMobile && isCameraActive && !hasImages) {
    return (
      <div className={styles.container}>
        <div className={styles.header}>
          <Button type="text" icon={<CloseOutlined />} onClick={handleCancel} className={styles.closeButton} />
          <Title level={4} className={styles.title}>
            Capture Delivery Note
          </Title>
        </div>

        <div className={styles.cameraContainer}>
          <video ref={videoRef} autoPlay playsInline muted className={styles.cameraPreview} />
          <canvas ref={canvasRef} style={{ display: 'none' }} />
        </div>

        <div className={styles.cameraControls}>
          <Button
            type="text"
            size="large"
            icon={<PictureOutlined />}
            onClick={handleOpenGallery}
            className={styles.galleryButton}
          >
            Gallery
          </Button>
          <Button
            type="primary"
            shape="circle"
            size="large"
            icon={<CameraOutlined />}
            onClick={handleCapture}
            className={styles.captureButtonLarge}
          />
          <div className={styles.placeholder} />
        </div>

        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          onChange={handleFileSelect}
          style={{ display: 'none' }}
        />
      </div>
    );
  }

  // Processing/Loading screen
  if (isProcessing) {
    return (
      <div className={styles.container}>
        <div className={styles.processingContainer}>
          {orderIdentifier && (
            <div className={styles.orderIdentifier}>
              <Title level={3} className={styles.orderTitle}>
                Identifying Order
              </Title>
            </div>
          )}
          <div className={styles.lottieContainer}>
            <Lottie animationData={loadingAnimation} loop={true} autoplay={true} className={styles.lottieAnimation} />
          </div>
          <Text className={styles.processingSubText}>This can take couple of seconds</Text>
        </div>
      </div>
    );
  }

  // Image preview and editing screen (with camera for retake)
  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <Button type="text" icon={<CloseOutlined />} onClick={handleCancel} className={styles.closeButton} />
        <Title level={4} className={styles.title}>
          Captured Delivery Note
        </Title>
      </div>

      <div
        className={isCameraActive ? styles.cameraContainer : styles.imageContainer}
        onTouchStart={isMobile && !isCameraActive ? handleTouchStart : undefined}
        onTouchMove={isMobile && !isCameraActive ? handleTouchMove : undefined}
        onTouchEnd={isMobile && !isCameraActive ? handleTouchEnd : undefined}
      >
        {isCameraActive ? (
          <>
            <video ref={videoRef} autoPlay playsInline muted className={styles.cameraPreview} />
            <canvas ref={canvasRef} style={{ display: 'none' }} />
          </>
        ) : hasImages ? (
          <img
            src={images[currentImageIndex]}
            alt={`Delivery note ${currentImageIndex + 1}`}
            className={styles.previewImage}
          />
        ) : (
          <div className={styles.noImage}>
            <CameraOutlined className={styles.noImageIcon} />
            <Text>No image captured</Text>
          </div>
        )}
      </div>

      {images.length > 1 && !isCameraActive && (
        <div className={styles.navigation}>
          <Button
            type="text"
            icon={<ArrowLeftIcon />}
            onClick={handlePrevious}
            disabled={currentImageIndex === 0}
            className={styles.navButton}
          />
          <Text className={styles.pageIndicator}>
            {currentImageIndex + 1} / {validImagesCount}
          </Text>
          <Button
            type="text"
            icon={<ArrowRightIcon />}
            onClick={handleNext}
            disabled={currentImageIndex === images.length - 1 || !images[currentImageIndex + 1]}
            className={styles.navButton}
          />
        </div>
      )}

      {isCameraActive ? (
        <div className={styles.cameraControls}>
          <Button
            type="text"
            size="large"
            icon={<PictureOutlined />}
            onClick={handleOpenGallery}
            className={styles.galleryButton}
          >
            Gallery
          </Button>
          <Button
            type="primary"
            shape="circle"
            size="large"
            icon={<CameraOutlined />}
            onClick={handleCapture}
            className={styles.captureButtonLarge}
          />
          <div className={styles.placeholder} />
        </div>
      ) : (
        <div className={styles.actions}>
          <div className={styles.secondaryActions}>
            <Button size="large" onClick={handleRetake} className={styles.retakeButton} disabled={isLoading}>
              {hasImages ? 'Retake' : 'Capture Image'}
            </Button>
            <Button size="large" onClick={handleCaptureNextPage} className={styles.nextPageButton} disabled={isLoading}>
              Capture Next Page
            </Button>
          </div>
          <Button
            type="primary"
            size="large"
            onClick={handleDone}
            loading={isLoading}
            className={styles.doneButton}
            block
          >
            Done ({validImagesCount} {validImagesCount === 1 ? 'image' : 'images'})
          </Button>
        </div>
      )}

      <input ref={fileInputRef} type="file" accept="image/*" onChange={handleFileSelect} style={{ display: 'none' }} />
    </div>
  );
};
